import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'
import { phq2Status } from '../../lib/functional'

const RECALL_WORDS = ['Apple', 'Table', 'Penny']

const HEARING = [
  { score: 1, label: 'Follows clearly' },
  { score: 2, label: 'Asks for repeats' },
  { score: 3, label: 'Significant difficulty' },
]
const VISION = [
  { score: 1, label: 'No concerns' },
  { score: 2, label: 'Some difficulty' },
  { score: 3, label: 'Significant difficulty' },
]
const PHQ2 = [
  { key: 'phq2_q1', q: 'Over the last 2 weeks, how often has she had little interest or pleasure in doing things?' },
  { key: 'phq2_q2', q: 'Over the last 2 weeks, how often has she felt down, depressed, or hopeless?' },
]
const PHQ2_OPTIONS = [
  { v: 0, label: 'Not at all' },
  { v: 1, label: 'Several days' },
  { v: 2, label: 'More than half' },
  { v: 3, label: 'Nearly every day' },
]

const todayISO = () => new Date().toLocaleDateString('en-CA')

export default function NurseAssessmentForm({ recipientId, onDone, onCancel }) {
  const { user } = useAuth()
  const toast = useToast()
  const [f, setF] = useState({
    assessment_date: todayISO(),
    mobility_tug_seconds: '',
    recalled: [],
    strength_chair_stands: '',
    hearing_score: null,
    vision_score: null,
    phq2_q1: null,
    phq2_q2: null,
    notes: '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }))

  const toggleWord = (w) =>
    setF((c) => ({
      ...c,
      recalled: c.recalled.includes(w)
        ? c.recalled.filter((x) => x !== w)
        : [...c.recalled, w],
    }))

  const phq = phq2Status(f.phq2_q1, f.phq2_q2)

  async function save() {
    const num = (v, float = false) => {
      if (v === '' || v == null) return null
      const n = float ? parseFloat(v) : parseInt(v, 10)
      return Number.isFinite(n) ? n : null
    }
    const payload = {
      care_recipient_id: recipientId,
      assessed_by: user.id,
      assessment_date: f.assessment_date || todayISO(),
      mobility_tug_seconds: num(f.mobility_tug_seconds, true),
      memory_recall_score: f.recalled.length,
      strength_chair_stands: num(f.strength_chair_stands),
      hearing_score: f.hearing_score,
      vision_score: f.vision_score,
      phq2_q1: f.phq2_q1,
      phq2_q2: f.phq2_q2,
      notes: f.notes.trim() || null,
    }
    setBusy(true)
    const { error } = await supabase.from('functional_assessments').insert(payload)
    setBusy(false)
    if (error) return toast.error(humanizeError(error))
    toast.success('Assessment saved.')
    onDone?.()
  }

  const Section = ({ emoji, title, hint, children }) => (
    <div className="card">
      <div className="row" style={{ gap: 8 }}>
        <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>{emoji}</span>
        <span style={{ fontWeight: 700 }}>{title}</span>
      </div>
      {hint && <div className="hint" style={{ marginTop: 4 }}>{hint}</div>}
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  )

  return (
    <div className="stack">
      <div className="spread">
        <h2 className="title" style={{ fontSize: 22 }}>Monthly assessment</h2>
        <button
          onClick={onCancel} aria-label="Close"
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 24, lineHeight: 1 }}
        >
          ×
        </button>
      </div>

      <div className="field">
        <label className="label" htmlFor="a-date">Assessment date</label>
        <input
          id="a-date" type="date" className="input"
          value={f.assessment_date} onChange={(e) => set('assessment_date', e.target.value)}
        />
      </div>

      <Section emoji="🚶" title="Mobility — Timed Up and Go" hint="Stand, walk 3 m, turn, return, sit. Under 12s is within normal range.">
        <input
          className="input" type="number" inputMode="decimal" step="0.1"
          placeholder="Seconds" aria-label="Seconds"
          value={f.mobility_tug_seconds}
          onChange={(e) => set('mobility_tug_seconds', e.target.value)}
        />
      </Section>

      <Section emoji="🧠" title="Memory — 3-word recall" hint="Say all three, wait a minute, then tap the ones recalled.">
        <div className="row wrap" style={{ gap: 8 }}>
          {RECALL_WORDS.map((w) => (
            <button
              key={w} type="button"
              className={`chip${f.recalled.includes(w) ? ' chip-on' : ''}`}
              aria-pressed={f.recalled.includes(w)}
              onClick={() => toggleWord(w)}
            >
              {f.recalled.includes(w) ? '✓ ' : ''}{w}
            </button>
          ))}
        </div>
        <div className="hint" style={{ marginTop: 8 }}>Score: {f.recalled.length} of 3</div>
      </Section>

      <Section emoji="💪" title="Strength — chair stand test" hint="Sit-to-stand repetitions in 30 seconds. 12+ is normal for women 70–79.">
        <input
          className="input" type="number" inputMode="numeric"
          placeholder="Count in 30s" aria-label="Chair stands in 30 seconds"
          value={f.strength_chair_stands}
          onChange={(e) => set('strength_chair_stands', e.target.value)}
        />
      </Section>

      <Section emoji="👂" title="Hearing" hint="Coordinator observation during the visit.">
        <div className="row wrap" style={{ gap: 8 }}>
          {HEARING.map((h) => (
            <button
              key={h.score} type="button"
              className={`chip${f.hearing_score === h.score ? ' chip-on' : ''}`}
              aria-pressed={f.hearing_score === h.score}
              onClick={() => set('hearing_score', f.hearing_score === h.score ? null : h.score)}
            >
              {h.label}
            </button>
          ))}
        </div>
      </Section>

      <Section emoji="👁️" title="Vision" hint="Self-reported.">
        <div className="row wrap" style={{ gap: 8 }}>
          {VISION.map((v) => (
            <button
              key={v.score} type="button"
              className={`chip${f.vision_score === v.score ? ' chip-on' : ''}`}
              aria-pressed={f.vision_score === v.score}
              onClick={() => set('vision_score', f.vision_score === v.score ? null : v.score)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </Section>

      <Section emoji="🙂" title="Mood — PHQ-2">
        {PHQ2.map((item, idx) => (
          <div key={item.key} style={{ marginBottom: idx === 0 ? 16 : 0 }}>
            <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 8 }}>{item.q}</div>
            <div className="row wrap" style={{ gap: 6 }}>
              {PHQ2_OPTIONS.map((o) => (
                <button
                  key={o.v} type="button"
                  className={`chip${f[item.key] === o.v ? ' chip-on' : ''}`}
                  style={{ fontSize: 13, minHeight: 40, padding: '6px 11px' }}
                  aria-pressed={f[item.key] === o.v}
                  onClick={() => set(item.key, f[item.key] === o.v ? null : o.v)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        {phq && (
          <div
            className="card"
            style={{
              marginTop: 14, padding: 12,
              background: phq.tone === 'bad' ? 'var(--amber-light)' : 'var(--green-light)',
              borderColor: 'transparent',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14 }}>{phq.text}</div>
          </div>
        )}
      </Section>

      <div className="field">
        <label className="label" htmlFor="a-notes">Notes</label>
        <textarea
          id="a-notes" className="textarea"
          placeholder="Anything the numbers don't capture."
          value={f.notes} onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      <button className="btn btn-block btn-lg" disabled={busy} onClick={save}>
        {busy ? <span className="spinner" /> : 'Save assessment'}
      </button>
    </div>
  )
}
